import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatLegacyInput as MatInput } from '@angular/material/legacy-input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AnalyticsService } from '@hq-core/analytics/analytics.service';
import { AuthService } from '@hq-core/auth/auth.service';
import { AnalyticsEvent, AnalyticsAction, AnalyticsCategory } from '@hq-core/models/analytics';
import { ProgressBarService } from '@cia-front-end-apps/shared/progress-bar';
import { expandCollapseAnimation } from '@hq-shared/models/expand-collapse-animation';
import { Observable, of, Subject } from 'rxjs';
import { delay, map, takeUntil, tap } from 'rxjs/operators';
import { PasswordChangeError, passwordResetErrorMessage, PasswordResetForm } from '../models/passwords';
import { routeParts } from '@hq-core/routes';

@Component({
    selector: 'hq-password-reset',
    templateUrl: './password-reset.component.html',
    styleUrls: ['./password-reset.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        expandCollapseAnimation
    ]
})
export class PasswordResetComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('formRef') formRef: NgForm;
    @ViewChild('verificationCode') verificationCodeElement: MatInput;
    @ViewChild('username') usernameElement: MatInput;

    formGroup: FormGroup;
    formButtonText: string;
    hasStartedReset = false;
    hasFormBeenSubmitted = false;
    isSubmissionInProgress = false;
    maskedEmailAddress: string;

    readonly logInRoute = routeParts.backOneLevel;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private cdRef: ChangeDetectorRef,
        private router: Router,
        private progressBarService: ProgressBarService,
        private snackbar: MatSnackBar,
        private authService: AuthService,
        private analyticsService: AnalyticsService
    ) { }

    ngOnInit(): void {
        this.formGroup = new FormGroup({
            username: new FormControl('', Validators.required)
        });

        this.formButtonText = 'Reset Password';
    }

    ngAfterViewInit(): void {
        // Workaround until https://github.com/angular/components/issues/12070 is fixed.
        // Everything but the element.focus() line can be removed then.
        of(true)
            .pipe(
                delay(0),
                takeUntil(this.unsubscribe)
            )
            .subscribe(() => {
                this.usernameElement.focus();
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    onAttachChildForm(name: string, control: AbstractControl): void {
        this.formGroup.setControl(name, control);
    }

    onSubmitForm(formValue: PasswordResetForm): void {
        this.hasFormBeenSubmitted = true;

        if (!this.formGroup.valid) {
            return;
        }

        this.progressBarService.startLoadingAnimation();
        this.isSubmissionInProgress = true;

        const request = this.hasStartedReset ?
            this.finishPasswordReset(formValue) :
            this.startPasswordReset(formValue);

        request
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: () => {
                    this.progressBarService.finishLoadingAnimation();

                    const event = new AnalyticsEvent({
                        action: AnalyticsAction.ResetPassword,
                        category: AnalyticsCategory.Authentication,
                        trackedValue: this.hasStartedReset ? 'started' : 'finished'
                    });
                    this.analyticsService.trackEvent(event);
                },
                error: (error: PasswordChangeError) => {
                    const errorMessage = passwordResetErrorMessage.get(error.name) ?? 'We were unable to reset your password.';
                    this.snackbar.open(errorMessage, 'DISMISS', { duration: 3500 });

                    this.markFormAsReadyForSubmission();
                    this.cdRef.detectChanges();

                    this.progressBarService.finishLoadingAnimation();

                    const event = new AnalyticsEvent({
                        action: AnalyticsAction.ResetPassword,
                        category: AnalyticsCategory.Authentication,
                        trackedValue: error.name
                    });
                    this.analyticsService.trackEvent(event);
                },
                complete: () => {
                    this.progressBarService.finishLoadingAnimation();
                }
            });
    }

    private startPasswordReset(formValue: PasswordResetForm): Observable<boolean> {
        return this.authService.startPasswordReset(formValue.username)
            .pipe(
                map(details => {
                    // Reset submitted state of the form
                    this.formRef.resetForm();
                    // Restore the username value so the user doesn't need to retype it
                    this.formGroup.reset(formValue);
                    this.formGroup.registerControl('verificationCode', new FormControl('', Validators.required));

                    this.markFormAsReadyForSubmission();
                    this.formButtonText = 'Save Password';
                    this.maskedEmailAddress = details.destination;
                    this.hasStartedReset = true;
                    this.cdRef.detectChanges();

                    this.verificationCodeElement.focus();

                    return !!(details);
                })
            );
    }

    private finishPasswordReset(formValue: PasswordResetForm): Observable<boolean> {
        return this.authService.finishPasswordReset(formValue)
            .pipe(
                tap(() => this.snackbar.open('Password Updated', 'DISMISS', { duration: 5000 })),
                // Artificial delay so the transition between updating and the login screen isn't too jarring
                delay(1000),
                tap(() => this.router.navigate([routeParts.backOneLevel]))
            );
    }

    private markFormAsReadyForSubmission(): void {
        this.hasFormBeenSubmitted = false;
        this.isSubmissionInProgress = false;
    }
}
